import { ITableDemandItem } from '@models/winnerSelection/ITableDemand';
import {
    IMatrixCell,
    IMatrixField,
    IMatrixFieldValue,
    IMatrixLot,
    IMatrixPosition,
    IWinnerMatrix,
    MatrixCompliance,
    MatrixPriceMode,
    MatrixResult,
} from '../WinnerSelection2.types';

export const cellKeyOf = (positionKey: string, participantKey: string) => `${positionKey}|${participantKey}`;

export interface IMatrixIndex {
    cells: Map<string, IMatrixCell>;
    positions: Map<string, IMatrixPosition>;
    lots: Map<string, IMatrixLot>;
    positionNumbers: Map<string, string>;
}

export const buildIndex = (matrix: IWinnerMatrix): IMatrixIndex => ({
    cells: new Map(matrix.cells.map((cell) => [cellKeyOf(cell.positionKey, cell.participantKey), cell])),
    positions: new Map(matrix.positions.map((position) => [position.key, position])),
    // Номер из закупки приоритетен; запасная сквозная нумерация не зависит от поиска.
    positionNumbers: new Map(
        matrix.positions.map((position, i) => [position.key, position.number?.trim() || String(i + 1)]),
    ),
    lots: new Map(matrix.lots.map((lot) => [lot.key, lot])),
});

export interface IVisibleLot {
    lot: IMatrixLot;
    /** Все позиции лота — по ним считаются итоги и оценка лота, независимо от поиска. */
    allPositions: IMatrixPosition[];
    /** Позиции, прошедшие поиск. */
    positions: IMatrixPosition[];
}

const normalize = (value: string) => value.toLocaleLowerCase('ru').replace(/ё/g, 'е').trim();

/**
 * Поиск по названию лота, названию, номеру и описанию позиции. Совпадение
 * названия лота оставляет все его позиции. Итоги лота поиск не меняет.
 */
export const filterLots = (matrix: IWinnerMatrix, index: IMatrixIndex, query: string): IVisibleLot[] => {
    const needle = normalize(query);
    return matrix.lots
        .map((lot) => {
            const allPositions = lot.positionKeys
                .map((key) => index.positions.get(key))
                .filter((position): position is IMatrixPosition => !!position);
            if (!needle || normalize(lot.title).includes(needle)) {
                return { lot, allPositions, positions: allPositions };
            }
            const positions = allPositions.filter((position) =>
                [position.name, index.positionNumbers.get(position.key) ?? '', position.description ?? ''].some(
                    (text) => normalize(text).includes(needle),
                ),
            );
            return { lot, allPositions, positions };
        })
        .filter((item) => item.positions.length > 0);
};

const quantityFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 6 });
const moneyFormat = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const formatQuantity = (value: number | null | undefined) =>
    value == null ? '—' : quantityFormat.format(value);

const CURRENCY_SIGNS: Record<string, string> = { RUB: '₽', USD: '$', EUR: '€', CNY: '¥' };
/** Код валюты ISO — знаком или заглавными; прочую единицу («тыс. руб.») — как задана. */
const currencySign = (currency: string) =>
    CURRENCY_SIGNS[currency.toUpperCase()] ?? (/^[a-z]{3}$/i.test(currency) ? currency.toUpperCase() : currency);

/** Неизвестная цена — прочерк; ноль остаётся нулём. */
export const formatMoney = (value: number | null | undefined, currency: string | null | undefined) => {
    if (value == null) return '—';
    const sign = currency ? currencySign(currency) : '';
    return sign ? `${moneyFormat.format(value)} ${sign}` : moneyFormat.format(value);
};

const numberFormats = new Map<string, Intl.NumberFormat>();
const numberFormat = (precision: number, fixed: boolean) => {
    const key = `${precision}:${fixed}`;
    let format = numberFormats.get(key);
    if (!format) {
        format = new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits: fixed ? precision : 0,
            maximumFractionDigits: precision,
        });
        numberFormats.set(key, format);
    }
    return format;
};

/** Значение настроенного поля; нет значения — прочерк, ноль остаётся нулём. */
export const formatFieldValue = (field: IMatrixField, value: IMatrixFieldValue | undefined) => {
    if (!value) return '—';
    if (value.number == null) return value.text?.trim() || '—';
    const money = field.format === 'money';
    const number = numberFormat(field.precision, money).format(value.number);
    if (!value.unit) return number;
    return `${number} ${money ? currencySign(value.unit) : value.unit}`;
};

/** Встроенный показатель ячейки — цена за единицу в режиме НДС закупки. */
export const UNIT_PRICE_METRIC = 'unitPrice';
export const UNIT_PRICE_TITLE = 'Цена за единицу';

const SCOPE_NOTE: Record<IMatrixField['scope'], string> = {
    unit: 'за единицу',
    position: 'за позицию',
    offer: 'по предложению',
};

/** Подпись под числом ячейки: уровень показателя и, если он зависит от НДС, режим закупки. */
export const metricNote = (metric: IMatrixField, mode: MatrixPriceMode) =>
    metric.vatAware ? `${SCOPE_NOTE[metric.scope]} · ${priceModeShort(mode)}` : SCOPE_NOTE[metric.scope];

export const priceModeLabel = (mode: MatrixPriceMode) =>
    mode === 'gross' ? 'Цены с НДС' : mode === 'net' ? 'Цены без НДС' : 'Режим НДС в закупке не задан';

export const priceModeShort = (mode: MatrixPriceMode) =>
    mode === 'gross' ? 'с НДС' : mode === 'net' ? 'без НДС' : 'НДС не задан';

export const resultLabel = (result: MatrixResult, text?: string | null) => {
    switch (result) {
        case 'main':
            return 'Основной';
        case 'reserve':
            return 'Резервный';
        case 'other':
            return text || 'Другой результат';
        default:
            return 'Не выбран';
    }
};

export const COMPLIANCE_LABEL: Record<MatrixCompliance, string> = {
    compliant: 'Соответствует',
    partial: 'Частично',
    noncompliant: 'Не соответствует',
    unanswered: 'Нет ответа',
    unknown: 'Ответ вне вариантов',
};

export const complianceAllowsSelection = (compliance: MatrixCompliance) =>
    compliance === 'compliant' || compliance === 'partial';

/** Сумма выбранного объёма; без цены или количества — неизвестна. */
export const cellAmount = (cell: IMatrixCell, quantity: number | null | undefined) =>
    cell.price == null || quantity == null ? null : Math.round(cell.price * quantity * 100) / 100;

/**
 * Объём больше потребности с учётом других назначений того же результата
 * по позиции — только предупреждение, сохранение разрешено.
 */
export const exceedsRequired = (
    matrix: IWinnerMatrix,
    cell: IMatrixCell,
    position: IMatrixPosition | undefined,
    result: MatrixResult,
    quantity: number | null,
): boolean => {
    if (quantity == null || position?.requiredQuantity == null || (result !== 'main' && result !== 'reserve'))
        return false;
    const others = matrix.cells
        .filter(
            (other) =>
                other.positionKey === cell.positionKey &&
                other.participantKey !== cell.participantKey &&
                other.result === result,
        )
        .reduce((sum, other) => sum + (other.effectiveQuantity ?? 0), 0);
    return others + quantity > position.requiredQuantity;
};

export interface ICriteriaGroup {
    category: string;
    items: ITableDemandItem[];
}

export const groupCriteria = (items: ITableDemandItem[], order: string[] | undefined): ICriteriaGroup[] => {
    const groups = new Map<string, ITableDemandItem[]>();
    items.forEach((item) => {
        const category = item.category || 'Прочие';
        groups.set(category, [...(groups.get(category) ?? []), item]);
    });
    const rank = (category: string) => {
        const position = order?.indexOf(category) ?? -1;
        return position < 0 ? Number.MAX_SAFE_INTEGER : position;
    };
    return [...groups.entries()]
        .map(([category, grouped]) => ({ category, items: grouped }))
        .sort((a, b) => rank(a.category) - rank(b.category));
};

const collectRevisions = (
    matrix: IWinnerMatrix,
    cell: IMatrixCell,
    revisionOf: (other: IMatrixCell) => string | undefined,
): Record<string, string> => {
    const revisions: Record<string, string> = {};
    for (const other of matrix.cells) {
        if (other.participantKey !== cell.participantKey || other.offerKey === cell.offerKey) continue;
        const revision = revisionOf(other);
        if (revision !== undefined) revisions[other.offerKey] = revision;
    }
    return revisions;
};

/** Остальные предложения той же заявки, где можно ответить по ТЗ: ключ предложения → ревизия. */
export const otherComplianceRevisions = (matrix: IWinnerMatrix, cell: IMatrixCell) =>
    collectRevisions(matrix, cell, (other) => (other.canEditCompliance ? other.revision : undefined));

/** Остальные предложения той же заявки, где можно править попозиционный критерий: ключ → ревизия критерия. */
export const otherCriterionRevisions = (matrix: IWinnerMatrix, cell: IMatrixCell, critId: string) =>
    collectRevisions(matrix, cell, (other) => {
        const criterion = other.criteria?.find((entry) => entry.item.critId === critId);
        return criterion?.value.allowEdit ? criterion.revision : undefined;
    });

/**
 * Цвет маркера контрагента для CSS. auto — так в справочнике записан «Белый»:
 * белый в обеих темах. Пустой и нераспознанный — без заливки.
 */
export const markerColorOf = (color: string | null | undefined): string | null => {
    const value = color?.trim();
    if (!value) return null;
    if (value.toLowerCase() === 'auto') return 'white';
    const supported =
        typeof CSS !== 'undefined' && typeof CSS.supports === 'function'
            ? CSS.supports('color', value)
            : /^[a-z]+$/i.test(value);
    return supported ? value : null;
};

export const decidedPositions = (matrix: IWinnerMatrix) =>
    matrix.positions.filter((position) =>
        matrix.cells.some((cell) => cell.positionKey === position.key && cell.result === 'main'),
    ).length;

/** Только служебные сообщения интерфейса, не значения и комментарии участников. */
export const formatMatrixMessage = (message: string): string => message.replace(/(?<!\.)\.(\s*)$/, '$1');

export const errorText = (reason: unknown): string => {
    if (Array.isArray(reason)) return formatMatrixMessage(reason.join(' '));
    if (reason instanceof Error) return formatMatrixMessage(reason.message);
    return typeof reason === 'string' && reason
        ? formatMatrixMessage(reason)
        : 'Не удалось выполнить действие. Повторите попытку';
};
